import { memo, useState, useMemo } from 'react';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { InputAdornment, ListItemText, Tooltip, Typography, useTheme, TextField, IconButton, Box } from '@mui/material';
import { palette } from '../theme/palette';
import { ReactComponent as ArrowIcon } from '../assets/icons/main/chevron-down-icon.svg';
import { ReactComponent as VerticalLine } from '../assets/icons/main/verticalLine.svg';
import { ReactComponent as CloseIcon } from '../assets/icons/main/close-icon.svg';
import CheckboxComponent from './Checkbox';
import { ReactComponent as SearchIcon } from '../assets/icons/main/search-icon.svg';

interface MultiSelectProps {
  options: any[];
  selectedOptions: string[];
  onChange: (data: any) => void;
  showSelectAllOption?: boolean;
  showSelectedLabels?: boolean;
  showSearch?: boolean;
  width?: string | number;
  placeholder?: string;
}

const Styles = {
  select: {
    '&.MuiInputAdornment-root': {
      position: 'absolute',
      right: '13%',
    },
  },
  listItemText: {
    '& .MuiListItemText-primary': { fontWeight: 400, textOverflow: 'ellipsis', overflow: 'hidden', fontSize: '16px' },
  },
};

const ITEM_HEIGHT = 40;
const ITEM_PADDING_TOP = 8;

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      borderRadius: '8px',
      boxShadow: '0px 0px 14px 0px #0000000D',
      backgroundColor: palette.primary.contrastText,
    }, 
  },
};

const MultiSelect = ({
  options = [],
  selectedOptions,
  onChange = () => { },
  showSelectAllOption,
  showSelectedLabels,
  showSearch = false,
  placeholder = 'Select',
  width = 242,
}: MultiSelectProps) => {
  const themeData: any = useTheme();
  const [searchTerm, setSearchTerm] = useState('');

  const handleChange = (event: SelectChangeEvent<typeof selectedOptions>) => {
    const {
      target: { value },
    } = event;
    if (value[value.length - 1] === 'search') return;
    let selectedValues = [];
    if (value[value.length - 1] === 'all') {
      selectedValues =
        options.length !== selectedOptions.length ? options.map(({ value }: { value: string }) => value) : [];
    } else {
      selectedValues = typeof value === 'string' ? value.split(',') : value;
    }

    onChange(selectedValues);
  };


  const filteredOptions = useMemo(() =>
    options.filter((option) =>
      option.label.toLowerCase().startsWith(searchTerm.toLowerCase())
    ), [options, searchTerm]
  );

  return (
    <FormControl sx={{ width }}>
      <Select
        multiple
        displayEmpty
        value={selectedOptions}
        onChange={handleChange}
        {...(selectedOptions.length && {
          startAdornment: (
            <InputAdornment position='end' sx={{ cursor: 'pointer', position: 'absolute', right: '15%', display: 'flex', alignItems: 'center' }}>
              <CloseIcon
                style={{ height: '18px', width: '18px', color: palette.info.dark, opacity: '0.7' }}
                onClick={() => {
                  onChange([]);
                }}
              />
              <VerticalLine style={{ height: '18px', margin: '0 9px 0 5px', color: themeData.palette.customBorderColor.main }} />
            </InputAdornment>
          ),
        })}
        renderValue={(selected) => {
          if (selected.length && showSelectedLabels) {
            const selectedLabels = selected
              .reduce((acc: any, item: any) => {
                const opt = options.find((option: any) => option.id === item)?.label;
                return [...acc, opt];
              }, [])
              .join(', ');

            return (
              <Tooltip
                title={
                  <Typography variant='subtitle1' style={{ fontSize: '1rem' }}>
                    {selectedLabels}
                  </Typography>
                }
                placement='right'
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      padding: '4px 16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    },
                  },
                }}
              >
                <span style={{ width: '100%', display: 'block', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                  {selectedLabels}
                </span>
              </Tooltip>
            );
          }
          return <i style={{ color: palette.info.dark, opacity: '0.5' }}>{placeholder}</i>;
        }}






        MenuProps={{
          variant: 'menu',
          PaperProps: {
            style: {
              ...MenuProps.PaperProps.style,
              width: width ?? 'auto',
            },
          },
        }}
        IconComponent={ArrowIcon}
        sx={Styles.select}
      >
        {showSearch && (
          <MenuItem disableGutters onKeyDown={(e) => e.stopPropagation()} value='search'
            sx={{
              padding: '11px 16px 0',
              '&.MuiMenuItem-root.Mui-focusVisible': {
                backgroundColor: 'transparent',
              },
              '&.MuiMenuItem-root:hover': {
                backgroundColor: 'transparent',
                borderBottom: 'red',
              }
            }}>
            <TextField
              placeholder="Search..."
              variant="standard"
              fullWidth
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                '& .MuiInputBase-input': {
                  padding: '0 0 12px',
                  fontSize: '1rem',
                  fontWeight: 400,
                  '&::placeholder': {
                    fontStyle: 'italic',
                  },
                },
                '& .MuiInput-root': {
                  '&:before': {
                    borderColor: '#D9dfed',
                  },
                  '&:after': {
                    borderColor: '#D9dfed',
                    borderWidth: '2px',
                  },
                  ':hover:not(.Mui-focused)': {
                    '&:before': {
                      borderColor: '#D9dfed',
                      borderWidth: '1px',
                    },
                  },
                },
              }}

              InputProps={{
                endAdornment: (
                  <InputAdornment position='end' sx={{ paddingBottom: '12px' }} >
                    <IconButton edge='end' onClick={() =>setSearchTerm(searchTerm)} >
                      <SearchIcon style={{ color: palette.info.dark, opacity: '0.7' }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </MenuItem>
        )}
        {showSelectAllOption && (
          <MenuItem value='all'>
            <CheckboxComponent checked={options.length === selectedOptions.length} />
            <ListItemText primary='Select All' sx={Styles.listItemText} />
          </MenuItem>
        )}
        {filteredOptions.map(({ label, value }: { label: string; value: string }) => (
          <MenuItem key={value} value={value} onKeyDown={(e) => e.stopPropagation()}>
            <CheckboxComponent checked={selectedOptions.includes(value)} />
            <Tooltip title={label} placement='right-end'>
              <ListItemText primary={label} sx={Styles.listItemText} />
            </Tooltip>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default memo(MultiSelect);
